/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * RouteMapArt / ElevationProfile
 * --------
 * Purely decorative artwork for the sign-in screen: a stylised city map with a
 * running route drawn across it, and the matching elevation profile.
 *
 * Why the geometry is generated rather than hand-drawn:
 *  - Roads meeting at odd angles and a trace that wanders a metre or two off the
 *    road centre line are what make a route read as a real GPS log. A lattice of
 *    right angles reads as a diagram. Both effects are cheaper to generate than
 *    to author by hand as path data.
 *  - Everything is computed once at module load from a fixed seed, so the map is
 *    identical on every render and every device — no layout shift, no flicker.
 *
 * Deliberately NO DOM measurement:
 *  Kilometre markers and direction chevrons are positioned by walking the
 *  polyline the curve is built from, not by calling SVGPathElement.getTotalLength().
 *  jsdom does not implement that method, so measuring in an effect would throw in
 *  the test environment. Catmull-Rom passes through every one of its control
 *  points, so interpolating along the polyline puts markers on the drawn curve.
 *
 * Theming:
 *  Colours are read from CSS custom properties (--sif-p, --sif-s) set by
 *  SignInForm on its root element, so the artwork follows the active theme
 *  without this component needing the theme context.
 */

import React from 'react';

// ── Canvas ────────────────────────────────────────────────
const MW = 480;
const MH = 300;
const ROUTE_KM = 12.4;

// ── Deterministic noise ───────────────────────────────────
function rng(seed) {
    return function () {
        seed = (seed * 1664525 + 1013904223) | 0;
        return ((seed >>> 8) & 0xffffff) / 0xffffff;
    };
}

/**
 * Catmull-Rom through every point, emitted as cubic beziers. The curve passes
 * through each control point, which is what lets markers be placed by
 * interpolating the underlying polyline.
 */
function catmull(p) {
    const n = p.length;
    let d = `M${p[0][0].toFixed(1)} ${p[0][1].toFixed(1)}`;
    for (let i = 0; i < n - 1; i++) {
        const p0 = p[Math.max(0, i - 1)];
        const p1 = p[i];
        const p2 = p[i + 1];
        const p3 = p[Math.min(n - 1, i + 2)];
        const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
        const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
        d += ` C${c1[0].toFixed(1)} ${c1[1].toFixed(1)} ${c2[0].toFixed(1)} ${c2[1].toFixed(1)} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
    }
    return d;
}

/**
 * Resample a road corridor into short steps and push each step slightly off the
 * centre line. The offset is scaled by sin(pi * t) so it falls to zero at both
 * ends of every segment — junctions stay crisp, straights wander.
 */
function wobble(pts, step, amp, seed) {
    const r = rng(seed);
    const out = [];
    for (let i = 0; i < pts.length - 1; i++) {
        const p = pts[i];
        const q = pts[i + 1];
        const dx = q[0] - p[0];
        const dy = q[1] - p[1];
        const len = Math.hypot(dx, dy);
        const nx = -dy / len;
        const ny = dx / len;
        const n = Math.max(2, Math.round(len / step));
        for (let k = 0; k < n; k++) {
            const t = k / n;
            const w = (r() - 0.5) * 2 * amp * Math.sin(Math.PI * t);
            out.push([p[0] + dx * t + nx * w, p[1] + dy * t + ny * w]);
        }
    }
    out.push(pts[pts.length - 1]);
    return out;
}

// ── The road network ──────────────────────────────────────
// Sixteen junctions meeting at irregular angles. No lattice, no right angles.
const J = {
    a: [64, 236], b: [58, 168], c: [96, 120], d: [150, 132],
    e: [142, 72], f: [214, 54], g: [286, 66], h: [316, 110],
    i: [382, 96], j: [420, 150], k: [396, 206], l: [330, 196],
    m: [300, 238], n: [228, 224], o: [168, 246], p: [104, 258],
};

// `arterial: true` roads get the heavier casing — these are the two the route
// spends most of its distance on.
const ROADS = [
    { p: [[30, 268], J.a, J.b, J.c] },
    { p: [J.c, J.d, [236, 150], J.h, [404, 128]] },
    { p: [J.b, J.c, J.e, [176, 36]] },
    { p: [[96, 88], J.e, J.f, J.g, J.h, J.i, [448, 78]], arterial: true },
    { p: [J.i, J.j, J.k, J.l] },
    { p: [J.l, J.m, J.n, J.o, J.p, J.a, [28, 224]], arterial: true },
    { p: [J.f, [222, 140], J.n] },
    { p: [J.p, [196, 182], J.h] },
    { p: [J.b, [168, 158], [300, 178], J.j] },
    { p: [[260, 290], [286, 214], [286, 66]] },
];

// The run: out along the northern arterial, back west along the southern one.
const WAYPOINTS = [
    J.a, J.b, J.c, J.d, J.e, J.f, J.g, J.h,
    J.i, J.j, J.k, J.l, J.m, J.n, J.o, J.p, [80, 250],
];

const TRACE = wobble(WAYPOINTS, 21, 2.4, 20260910);
const ROUTE_D = catmull(TRACE);

// ── Marker placement, by walking the polyline ─────────────
const SEG = [];
let traceLength = 0;
for (let i = 0; i < TRACE.length - 1; i++) {
    const len = Math.hypot(TRACE[i + 1][0] - TRACE[i][0], TRACE[i + 1][1] - TRACE[i][1]);
    SEG.push({ from: TRACE[i], to: TRACE[i + 1], len, start: traceLength });
    traceLength += len;
}

/** Point and heading at a given distance along the trace. */
function atLength(dist) {
    const d = Math.max(0, Math.min(traceLength, dist));
    let s = SEG[SEG.length - 1];
    for (let i = 0; i < SEG.length; i++) {
        if (d <= SEG[i].start + SEG[i].len) { s = SEG[i]; break; }
    }
    const t = s.len === 0 ? 0 : (d - s.start) / s.len;
    return {
        x: s.from[0] + (s.to[0] - s.from[0]) * t,
        y: s.from[1] + (s.to[1] - s.from[1]) * t,
        deg: (Math.atan2(s.to[1] - s.from[1], s.to[0] - s.from[0]) * 180) / Math.PI,
    };
}

// Direction chevrons roughly every 11% of the route.
const CHEVRONS = [];
for (let f = 0.055; f < 1; f += 0.11) CHEVRONS.push(atLength(traceLength * f));

// Kilometre markers. Stops at 10 so the last pin doesn't collide with the finish.
const KM_MARKS = [];
for (let k = 2; k <= 10; k += 2) {
    KM_MARKS.push({ k, ...atLength(traceLength * (k / ROUTE_KM)) });
}

const START = atLength(0);
const FINISH = atLength(traceLength);

// A dash slightly longer than the curve guarantees a single dash (never a
// repeat) while the draw-on animation runs.
const DASH = Math.round(traceLength * 1.06);

/**
 * The map. Decorative: announced as a single image, inner labels are not read out.
 */
export function RouteMapArt() {
    return (
        <svg
            className="sif-map"
            viewBox={`0 0 ${MW} ${MH}`}
            role="img"
            aria-label="Illustrative route map: a 12.4 kilometre loop through the city, past the park and the lake"
        >
            <defs>
                <linearGradient id="sifRouteGradient" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="var(--sif-p)" />
                    <stop offset="100%" stopColor="var(--sif-s)" />
                </linearGradient>
            </defs>

            <rect className="sif-plot" x="0" y="0" width={MW} height={MH} />

            {/* road casings, then the road surfaces on top */}
            {ROADS.map((r, i) => (
                <path key={`c${i}`} className={r.arterial ? 'sif-artc' : 'sif-stc'} d={catmull(r.p)} />
            ))}
            {ROADS.map((r, i) => (
                <path key={`s${i}`} className={r.arterial ? 'sif-art' : 'sif-st'} d={catmull(r.p)} />
            ))}

            <path
                className="sif-park"
                d="M118 148 q46-22 84-8 q34 13 30 44 q-4 32-44 40 q-44 9-70-14 q-24-22 0-62z"
            />
            <path className="sif-trail" d="M126 172 q34-14 62 6 q26 18 52 8" />
            <path
                className="sif-water"
                d="M340 232 q28-14 52 2 q22 15 14 32 q-9 18-40 14 q-32-5-38-24 q-5-16 12-24z"
            />

            <text className="sif-alab" x="176" y="196">Cubbon Park</text>
            <text className="sif-alab" x="376" y="262">Ulsoor Lake</text>
            <text className="sif-slab" x="196" y="46" transform="rotate(-9 196 46)">Queen&apos;s Rd</text>
            <text className="sif-slab" x="196" y="258" transform="rotate(6 196 258)">MG Rd</text>

            <path className="sif-case" d={ROUTE_D} />
            <path
                className="sif-line"
                d={ROUTE_D}
                style={{ strokeDasharray: DASH, '--sif-dash': DASH }}
            />

            {CHEVRONS.map((c, i) => (
                <path
                    key={`v${i}`}
                    className="sif-chev"
                    d="M-2.6 -3 L2.2 0 L-2.6 3"
                    transform={`translate(${c.x.toFixed(1)} ${c.y.toFixed(1)}) rotate(${c.deg.toFixed(1)})`}
                />
            ))}

            {KM_MARKS.map((m) => (
                <g key={`k${m.k}`}>
                    <circle className="sif-km" cx={m.x.toFixed(1)} cy={m.y.toFixed(1)} r="9" />
                    <text className="sif-kmt" x={m.x.toFixed(1)} y={m.y.toFixed(1)}>{m.k}</text>
                </g>
            ))}

            <circle className="sif-pin" cx={START.x.toFixed(1)} cy={START.y.toFixed(1)} r="8.5" />
            <text className="sif-kmt sif-kmt-light" x={START.x.toFixed(1)} y={START.y.toFixed(1)}>S</text>
            <circle className="sif-fin" cx={FINISH.x.toFixed(1)} cy={FINISH.y.toFixed(1)} r="8.5" />
            <text className="sif-kmt sif-kmt-light" x={FINISH.x.toFixed(1)} y={FINISH.y.toFixed(1)}>F</text>

            <g className="sif-comp" transform="translate(452 268)">
                <circle r="12" />
                <path d="M0 -7 L3.4 3 L0 .8 L-3.4 3 Z" />
                <text y="8.6">N</text>
            </g>
        </svg>
    );
}

// ── Elevation profile ─────────────────────────────────────
// Summed sines: one long climb, one rolling mid-section, one fine ripple. Shaped
// so it reads like a route profile rather than a generic sparkline.
const EW = 360;
const EH = 52;
function buildElevation() {
    const pts = [];
    for (let i = 0; i <= 70; i++) {
        const t = i / 70;
        const h =
            Math.sin(t * Math.PI * 1.7) * 0.42 +
            Math.sin(t * Math.PI * 5.2) * 0.13 +
            Math.sin(t * Math.PI * 11) * 0.05 +
            0.46;
        pts.push([t * EW, EH - 8 - h * (EH - 16)]);
    }
    let line = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
    pts.slice(1).forEach((q) => { line += ` L${q[0].toFixed(1)} ${q[1].toFixed(1)}`; });
    return { line, area: `${line} L${EW} ${EH} L0 ${EH} Z` };
}
const ELEV = buildElevation();
const SPLITS = [];
for (let k = 1; k < ROUTE_KM; k += 1) SPLITS.push(((k * EW) / ROUTE_KM).toFixed(1));

export function ElevationProfile() {
    return (
        <svg className="sif-elev-svg" viewBox={`0 0 ${EW} ${EH}`} preserveAspectRatio="none" aria-hidden="true">
            <defs>
                <linearGradient id="sifElevGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--sif-p)" stopOpacity="0.34" />
                    <stop offset="100%" stopColor="var(--sif-p)" stopOpacity="0.02" />
                </linearGradient>
            </defs>
            {SPLITS.map((x) => (
                <line key={x} className="sif-split" x1={x} y1="0" x2={x} y2={EH} />
            ))}
            <path className="sif-elev-area" d={ELEV.area} />
            <path className="sif-elev-line" d={ELEV.line} />
        </svg>
    );
}

/*
 * The generated geometry, shared with the athlete profile header
 * (pages/feed/profile/ProfileRouteArt.js) so both screens draw the same route
 * and elevation profile from one copy of the code.
 */
export const ROUTE_ART = {
    MW, MH, ROUTE_KM, ROADS, ROUTE_D, CHEVRONS, KM_MARKS, START, FINISH, DASH,
    EW, EH, ELEV, SPLITS, catmull,
};

export default RouteMapArt;
